import { usePostHog } from 'posthog-js/react';
import { FC } from 'react';
import { useParams } from 'react-router-dom';

import { useCartContext } from '../../hooks/contexts/cart.context';
import { useEventContext } from '../../hooks/contexts/event.context';
import { useI18nContext } from '../../hooks/contexts/i18n.context';
import { useModalContext } from '../../hooks/contexts/modal.context';
import BulletQuantity from '../BulletQuantity';
import Popover from '../Popover';
import { ResaleDateModal } from '../ResaleDateModal';
import * as Style from './styles.carousel';

type CarouselItemProps = {
  item: {
    id: number;
    selectedId: number;
    title: string;
    subtitle?: string;
    finished: boolean;
    isPassport?: boolean;
    hasTicketAvailable: boolean;
    hasResale?: boolean;
    resaleCount?: number;
    definitiveSessionId: string;
    callback: () => void;
  };
}

const CarouselItem: FC<CarouselItemProps> = ({ item }) => {
  const posthog = usePostHog();
  const { selectIdDateCarousel } = useEventContext();
  const { ticketsPerSession } = useCartContext();
  const { translate } = useI18nContext();
  const { defineTitleModal, setContentModal, toggleModal } = useModalContext();
  const { eventId } = useParams<{ eventId: string }>();

  const selected = item.id === item.selectedId;
  const disabled = item.finished || (!item.hasTicketAvailable && !item.hasResale);

  let title = item.title;
  if (item.title.length >= 30) title = `${item.title.slice(0, 30)}...`;
  else {
    const [day, month, year] = item.title.split(' ');
    if (Boolean(day) && Boolean(month) && Boolean(year))
      title = `${day} ${month.slice(0, 3)} ${year}`;
  }

  const selectDate = (item: CarouselItemProps['item']) => {
    if (item.hasResale) {
      posthog?.capture('purchase:ticket_selection_resale_view', { eventId });
      defineTitleModal(translate(['RESALE', 'DATE_MODAL', 'TITLE']));
      setContentModal(<ResaleDateModal />);
      toggleModal({ open : true });
      return;
    }
    selectIdDateCarousel(item.id);
    let captureEvent = 'purchase:ticket_selection_session_view';
    if (item.isPassport) {
      captureEvent = 'purchase:ticket_selection_passport_view';
    }
    if (title === 'COMBO') {
      captureEvent = 'purchase:ticket_selection_combo_view';
    }
    posthog?.capture(captureEvent);
  };

  return (
    <Style.Item
      id={`carousel-item-${item.id}`}
      data-testid={`carousel-item-${item.id}`}
      $selected={selected}
      $disabled={disabled}
      $isLarge={item.title.length >= 30}
      onClick={() => {
        if (disabled) return;
        selectDate(item);
      }}
    >
      {item.hasResale && Boolean(item.resaleCount) ? (
        <BulletQuantity quantity={item.resaleCount ?? 0} />
      ) : (
        Boolean(ticketsPerSession[item.definitiveSessionId]) && (
          <BulletQuantity quantity={ticketsPerSession[item.definitiveSessionId]} />
        )
      )}
      <Popover
        render={item.title.length >= 30}
        id="summary-popover-info"
        text={item.title}>
        <h1>{title}</h1>
      </Popover>

      {item.subtitle && <span>{item.subtitle.toLowerCase()}</span>}
    </Style.Item>
  );
};

export default CarouselItem;
